import { useState, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
    useTeamMembers,
    useRemoveMember,
    useUpdateMember,
    useInviteMember,
    useRoles,
    usePendingInvitations,
    useCancelInvitation,
    useResendInvitation,
    type TeamMember,
    type PendingInvitation,
} from '@/features/settings/api/useTeam'
import { useAuthStore } from '@/lib/auth/authStore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
    Search,
    UserPlus,
    MoreVertical,
    Shield,
    Trash2,
    Loader2,
    RefreshCw,
    UserCheck,
    Crown,
    Briefcase,
    Eye,
    Clock,
    Send,
    XCircle,
} from 'lucide-react'

// ---------------------------------------------------------------------------
// Role icon mapping
// ---------------------------------------------------------------------------

const roleIcons: Record<string, typeof UserCheck> = {
    org_admin: Crown,
    admin: Crown,
    c_suite: Crown,
    executive: Briefcase,
    director: Briefcase,
    manager: Briefcase,
    test_manager: Shield,
    engineer: UserCheck,
    tester: UserCheck,
    viewer: Eye,
    member: UserCheck,
}

const ROLE_OPTIONS = [
    'org_admin',
    'admin',
    'c_suite',
    'executive',
    'director',
    'manager',
    'test_manager',
    'engineer',
    'tester',
    'viewer',
]

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function TeamTableSkeleton() {
    return (
        <Card>
            <CardHeader>
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-4 w-64 mt-1" />
            </CardHeader>
            <CardContent className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                ))}
            </CardContent>
        </Card>
    )
}

// ---------------------------------------------------------------------------
// Error state
// ---------------------------------------------------------------------------

function TeamErrorState({ onRetry }: { onRetry: () => void }) {
    return (
        <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-lg font-medium mb-1">Failed to load team members</p>
                <p className="text-sm text-muted-foreground mb-4">
                    This may be a temporary network issue or a permissions problem.
                </p>
                <Button variant="outline" onClick={onRetry}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Retry
                </Button>
            </CardContent>
        </Card>
    )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function TeamPage() {
    const queryClient = useQueryClient()
    const { user: currentUser } = useAuthStore()
    const orgId = currentUser?.organization_id ?? ''

    const {
        data: members,
        isLoading,
        isError,
        refetch,
    } = useTeamMembers(orgId)

    const { data: roles } = useRoles()

    const removeTeamMember = useRemoveMember(orgId)
    const updateTeamMember = useUpdateMember(orgId)
    const inviteUser = useInviteMember(orgId)

    const {
        data: pendingInvitations,
        isLoading: invitationsLoading,
    } = usePendingInvitations(orgId)

    const cancelInvitation = useCancelInvitation(orgId)
    const resendInvitation = useResendInvitation(orgId)

    // -- Local state -----------------------------------------------------------

    const [searchQuery, setSearchQuery] = useState('')
    const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null)
    const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false)
    const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false)
    const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false)
    const [newRole, setNewRole] = useState<string>('engineer')
    const [inviteData, setInviteData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        role: 'engineer' as string,
    })

    // -- Derived ---------------------------------------------------------------

    const filteredMembers = useMemo(() => {
        if (!members) return []
        if (!searchQuery.trim()) return members
        const q = searchQuery.toLowerCase()
        return members.filter(
            (m) =>
                m.email.toLowerCase().includes(q) ||
                (m.first_name?.toLowerCase() ?? '').includes(q) ||
                (m.last_name?.toLowerCase() ?? '').includes(q) ||
                m.login.toLowerCase().includes(q) ||
                m.role.toLowerCase().includes(q)
        )
    }, [members, searchQuery])

    // -- Handlers --------------------------------------------------------------

    const openRemoveDialog = (member: TeamMember) => {
        setSelectedMember(member)
        setIsRemoveDialogOpen(true)
    }

    const openRoleDialog = (member: TeamMember) => {
        setSelectedMember(member)
        setNewRole(member.role)
        setIsRoleDialogOpen(true)
    }

    const handleRemoveMember = async () => {
        if (!selectedMember) return
        try {
            await removeTeamMember.mutateAsync(selectedMember.id)
            toast.success(`${selectedMember.email} has been removed from the team`)
            setIsRemoveDialogOpen(false)
            setSelectedMember(null)
        } catch (err: any) {
            toast.error(err.response?.data?.detail || 'Failed to remove team member')
        }
    }

    const handleUpdateRole = async () => {
        if (!selectedMember || !roles) return
        const matchedRole = roles.find((r: any) => r.name === newRole)
        if (!matchedRole) {
            toast.error('Selected role not found')
            return
        }
        try {
            await updateTeamMember.mutateAsync({
                id: selectedMember.id,
                role_id: matchedRole.id,
            })
            toast.success(`Role updated for ${selectedMember.email}`)
            setIsRoleDialogOpen(false)
            setSelectedMember(null)
        } catch (err: any) {
            toast.error(err.response?.data?.detail || 'Failed to update role')
        }
    }

    const handleInviteUser = async () => {
        if (!inviteData.email.trim()) {
            toast.error('Email is required')
            return
        }
        try {
            const result = await inviteUser.mutateAsync({
                email: inviteData.email.trim(),
                first_name: inviteData.first_name.trim() || undefined,
                last_name: inviteData.last_name.trim() || undefined,
                role: inviteData.role || undefined,
            })
            if (result.email_sent === false) {
                toast.warning(
                    `Invitation created for ${inviteData.email} but email delivery failed. You can resend from the pending list.`
                )
            } else {
                toast.success(`Invitation sent to ${inviteData.email}`)
            }
            queryClient.invalidateQueries({ queryKey: ['pendingInvitations', orgId] })
            setIsInviteDialogOpen(false)
            setInviteData({ email: '', first_name: '', last_name: '', role: 'engineer' })
        } catch (err: any) {
            toast.error(err.response?.data?.detail || 'Failed to send invitation')
        }
    }

    const handleCancelInvitation = async (inv: PendingInvitation) => {
        try {
            await cancelInvitation.mutateAsync(inv.id)
            toast.success(`Invitation to ${inv.email} cancelled`)
        } catch (err: any) {
            toast.error(err.response?.data?.detail || 'Failed to cancel invitation')
        }
    }

    const handleResendInvitation = async (inv: PendingInvitation) => {
        try {
            const result = await resendInvitation.mutateAsync(inv.id)
            if (result.email_sent) {
                toast.success(`Invitation resent to ${inv.email}`)
            } else {
                toast.warning(
                    `Invitation extended but email delivery failed for ${inv.email}`
                )
            }
        } catch (err: any) {
            toast.error(err.response?.data?.detail || 'Failed to resend invitation')
        }
    }

    // -- Render ----------------------------------------------------------------

    if (isLoading) return <TeamTableSkeleton />

    if (isError) return <TeamErrorState onRetry={() => refetch()} />

    const memberName = (m: TeamMember) =>
        m.first_name && m.last_name
            ? `${m.first_name} ${m.last_name}`
            : m.login

    return (
        <div className="space-y-6">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="relative w-full sm:w-72">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search members…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9"
                    />
                </div>
                <Button onClick={() => setIsInviteDialogOpen(true)}>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Invite User
                </Button>
            </div>

            {/* ------------------------------------------------------------------ */}
            {/* Members table                                                       */}
            {/* ------------------------------------------------------------------ */}
            <Card>
                <CardHeader>
                    <CardTitle>
                        Team Members
                        {members && (
                            <Badge variant="secondary" className="ml-3 text-xs font-normal">
                                {filteredMembers.length}
                                {searchQuery && ` of ${members.length}`}
                            </Badge>
                        )}
                    </CardTitle>
                    <CardDescription>
                        People who have access to your organization
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {filteredMembers.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            {searchQuery
                                ? 'No members match your search'
                                : 'No team members yet. Invite someone to get started.'}
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>User</TableHead>
                                    <TableHead>Role</TableHead>
                                    <TableHead>Joined</TableHead>
                                    <TableHead className="w-[60px]" />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredMembers.map((member) => {
                                    const isCurrentUser = member.id === currentUser?.id
                                    const RoleIcon = roleIcons[member.role] || UserCheck

                                    return (
                                        <TableRow key={member.id}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary">
                                                        {(member.first_name?.[0] || member.login[0]).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div className="font-medium">
                                                            {memberName(member)}
                                                            {isCurrentUser && (
                                                                <Badge variant="outline" className="ml-2 text-[10px]">
                                                                    You
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <div className="text-sm text-muted-foreground">
                                                            {member.email}
                                                        </div>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant={
                                                        member.role === 'org_admin' || member.role === 'admin'
                                                            ? 'default'
                                                            : 'secondary'
                                                    }
                                                >
                                                    <RoleIcon className="h-3 w-3 mr-1" />
                                                    {member.role.replace('_', ' ')}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {new Date(member.joined_at).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell>
                                                {!isCurrentUser && (
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="icon">
                                                                <MoreVertical className="h-4 w-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                                            <DropdownMenuSeparator />
                                                            <DropdownMenuItem
                                                                onClick={() => openRoleDialog(member)}
                                                            >
                                                                <Shield className="h-4 w-4 mr-2" />
                                                                Change Role
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem
                                                                onClick={() => openRemoveDialog(member)}
                                                                className="text-destructive"
                                                            >
                                                                <Trash2 className="h-4 w-4 mr-2" />
                                                                Remove
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* ------------------------------------------------------------------ */}
            {/* Pending Invitations                                                 */}
            {/* ------------------------------------------------------------------ */}
            {!invitationsLoading && pendingInvitations && pendingInvitations.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Clock className="h-5 w-5 text-amber-500" />
                            Pending Invitations
                            <Badge variant="outline" className="ml-2 text-xs font-normal">
                                {pendingInvitations.length}
                            </Badge>
                        </CardTitle>
                        <CardDescription>
                            Users who have been invited but haven&apos;t accepted yet
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Role</TableHead>
                                    <TableHead>Invited</TableHead>
                                    <TableHead>Expires</TableHead>
                                    <TableHead className="w-[60px]" />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {pendingInvitations.map((inv) => {
                                    const isExpiringSoon =
                                        inv.expires_at &&
                                        new Date(inv.expires_at).getTime() - Date.now() <
                                        2 * 24 * 60 * 60 * 1000

                                    return (
                                        <TableRow key={inv.id} className="opacity-80">
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center">
                                                        <Clock className="h-4 w-4 text-amber-600" />
                                                    </div>
                                                    <div>
                                                        <div className="font-medium">{inv.email}</div>
                                                        <Badge
                                                            variant="outline"
                                                            className="text-[10px] text-amber-600 border-amber-300"
                                                        >
                                                            Pending
                                                        </Badge>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {inv.first_name || inv.last_name
                                                    ? `${inv.first_name || ''} ${inv.last_name || ''}`.trim()
                                                    : '—'}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="secondary" className="capitalize">
                                                    {(inv.role_name || 'member').replace('_', ' ')}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {inv.invited_at
                                                    ? new Date(inv.invited_at).toLocaleDateString()
                                                    : '—'}
                                            </TableCell>
                                            <TableCell>
                        <span
                            className={
                                isExpiringSoon
                                    ? 'text-amber-600 font-medium'
                                    : 'text-muted-foreground'
                            }
                        >
                          {inv.expires_at
                              ? new Date(inv.expires_at).toLocaleDateString()
                              : '—'}
                        </span>
                                            </TableCell>
                                            <TableCell>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon">
                                                            <MoreVertical className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem
                                                            onClick={() => handleResendInvitation(inv)}
                                                            disabled={resendInvitation.isPending}
                                                        >
                                                            <Send className="h-4 w-4 mr-2" />
                                                            Resend Invitation
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            onClick={() => handleCancelInvitation(inv)}
                                                            disabled={cancelInvitation.isPending}
                                                            className="text-destructive"
                                                        >
                                                            <XCircle className="h-4 w-4 mr-2" />
                                                            Cancel Invitation
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {/* ------------------------------------------------------------------ */}
            {/* Remove Member Dialog                                                */}
            {/* ------------------------------------------------------------------ */}
            <Dialog open={isRemoveDialogOpen} onOpenChange={setIsRemoveDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Remove Team Member</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to remove{' '}
                            <span className="font-medium text-foreground">
                {selectedMember?.email}
              </span>{' '}
                            from the team? They will lose access to the organization.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsRemoveDialogOpen(false)}
                            disabled={removeTeamMember.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleRemoveMember}
                            disabled={removeTeamMember.isPending}
                        >
                            {removeTeamMember.isPending && (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            Remove
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ------------------------------------------------------------------ */}
            {/* Change Role Dialog                                                  */}
            {/* ------------------------------------------------------------------ */}
            <Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Change Role</DialogTitle>
                        <DialogDescription>
                            Update the role for{' '}
                            <span className="font-medium text-foreground">
                {selectedMember?.email}
              </span>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Label htmlFor="new-role">Role</Label>
                        <Select value={newRole} onValueChange={setNewRole}>
                            <SelectTrigger id="new-role">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {ROLE_OPTIONS.map((role) => {
                                    const Icon = roleIcons[role] || UserCheck
                                    return (
                                        <SelectItem key={role} value={role}>
                                            <div className="flex items-center gap-2">
                                                <Icon className="h-4 w-4 shrink-0" />
                                                <span className="capitalize">
                          {role.replace('_', ' ')}
                        </span>
                                            </div>
                                        </SelectItem>
                                    )
                                })}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsRoleDialogOpen(false)}
                            disabled={updateTeamMember.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUpdateRole}
                            disabled={updateTeamMember.isPending}
                        >
                            {updateTeamMember.isPending && (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            Update Role
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* ------------------------------------------------------------------ */}
            {/* Invite User Dialog                                                  */}
            {/* ------------------------------------------------------------------ */}
            <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Invite Team Member</DialogTitle>
                        <DialogDescription>
                            Invite a new member to your organization. They will receive an
                            email invitation to join.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label htmlFor="invite-email">Email *</Label>
                            <Input
                                id="invite-email"
                                type="email"
                                placeholder="user@example.com"
                                value={inviteData.email}
                                onChange={(e) =>
                                    setInviteData({ ...inviteData, email: e.target.value })
                                }
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="invite-first-name">First Name</Label>
                                <Input
                                    id="invite-first-name"
                                    placeholder="John"
                                    value={inviteData.first_name}
                                    onChange={(e) =>
                                        setInviteData({
                                            ...inviteData,
                                            first_name: e.target.value,
                                        })
                                    }
                                />
                            </div>
                            <div>
                                <Label htmlFor="invite-last-name">Last Name</Label>
                                <Input
                                    id="invite-last-name"
                                    placeholder="Doe"
                                    value={inviteData.last_name}
                                    onChange={(e) =>
                                        setInviteData({
                                            ...inviteData,
                                            last_name: e.target.value,
                                        })
                                    }
                                />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="invite-role">Role</Label>
                            <Select
                                value={inviteData.role}
                                onValueChange={(v) =>
                                    setInviteData({ ...inviteData, role: v })
                                }
                            >
                                <SelectTrigger id="invite-role">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {ROLE_OPTIONS.map((role) => {
                                        const Icon = roleIcons[role] || UserCheck
                                        return (
                                            <SelectItem key={role} value={role}>
                                                <div className="flex items-center gap-2">
                                                    <Icon className="h-4 w-4 shrink-0" />
                                                    <span className="capitalize">
                            {role.replace('_', ' ')}
                          </span>
                                                </div>
                                            </SelectItem>
                                        )
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsInviteDialogOpen(false)}
                            disabled={inviteUser.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleInviteUser}
                            disabled={inviteUser.isPending || !inviteData.email.trim()}
                        >
                            {inviteUser.isPending && (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            Send Invitation
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
